import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Container, Paper } from '@mui/material';
import { useNavigate } from 'react-router';
import moment from 'moment';
import * as XLSX from 'xlsx';

import { historyApi } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifySuccess, globalNotifyError } from 'views/utilities/AlertToast';

// Subcomponents
import TransactionHistoryBanner from './components/transaction-history/TransactionHistoryBanner';
import TransactionHistoryStats from './components/transaction-history/TransactionHistoryStats';
import TransactionHistoryToolbar from './components/transaction-history/TransactionHistoryToolbar';
import TransactionHistoryTable from './components/transaction-history/TransactionHistoryTable';

const TransactionHistory = () => {
    const navigate = useNavigate();

    // ─── Filter States ────────────────────────────────────────────────
    const [fromDate, setFromDate] = useState(() => moment().startOf('month').format('YYYY-MM-DD'));
    const [toDate, setToDate] = useState(() => moment().format('YYYY-MM-DD'));
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // ─── Pagination States (Backend 1-indexed, MUI TablePagination 0-indexed)
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    // ─── Data & Loading States ────────────────────────────────────────
    const [transactions, setTransactions] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // ─── Debounce Search Term (300ms) ─────────────────────────────────
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // ─── Fetch Transactions API ───────────────────────────────────────
    const fetchTransactionHistory = useCallback(
        async (customPage = page, customPerPage = rowsPerPage, customFrom = fromDate, customTo = toDate) => {
            setLoading(true);
            try {
                const payload = {
                    from_date: customFrom || moment().startOf('month').format('YYYY-MM-DD'),
                    to_date: customTo || moment().format('YYYY-MM-DD'),
                    page: customPage + 1,
                    per_page: customPerPage
                };

                const response = await historyApi(payload);

                let list = [];
                let count = 0;
                const resData = response?.data;

                if (resData?.status === true || resData?.success === true || response?.status === 200) {
                    if (Array.isArray(resData?.data)) {
                        list = resData.data;
                    } else if (Array.isArray(resData?.data?.data)) {
                        list = resData.data.data;
                    } else if (Array.isArray(resData?.records)) {
                        list = resData.records;
                    } else if (Array.isArray(resData)) {
                        list = resData;
                    }

                    count = resData?.pagination?.total ?? resData?.data?.total ?? resData?.total ?? resData?.total_count ?? list.length;
                }

                setTransactions(Array.isArray(list) ? list : []);
                setTotalCount(Number(count) || (Array.isArray(list) ? list.length : 0));
            } catch (error) {
                console.error('Failed to fetch transaction history:', error);
                setTransactions([]);
                setTotalCount(0);
                handleCatchError(error);
            } finally {
                setLoading(false);
            }
        },
        [fromDate, toDate, page, rowsPerPage]
    );

    useEffect(() => {
        fetchTransactionHistory(page, rowsPerPage, fromDate, toDate);
    }, [page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Filter & Search Handling ─────────────────────────────────────
    const handleApplyFilter = () => {
        setPage(0);
        fetchTransactionHistory(0, rowsPerPage, fromDate, toDate);
    };

    const handleResetFilter = () => {
        const defaultFrom = moment().startOf('month').format('YYYY-MM-DD');
        const defaultTo = moment().format('YYYY-MM-DD');
        setFromDate(defaultFrom);
        setToDate(defaultTo);
        setSearchTerm('');
        setDebouncedSearch('');
        setPage(0);
        fetchTransactionHistory(0, rowsPerPage, defaultFrom, defaultTo);
    };

    // Client-side search filtering over current records
    const displayedTransactions = useMemo(() => {
        if (!Array.isArray(transactions)) return [];
        const query = debouncedSearch.toLowerCase().trim();
        if (!query) return transactions;

        return transactions.filter((txn) => {
            if (!txn) return false;
            const email = String(txn.email || '').toLowerCase();
            const orderId = String(txn.order_id || '').toLowerCase();
            const rrn = String(txn.rrn || '').toLowerCase();
            const status = String(txn.status || '').toLowerCase();
            const amount = String(txn.amount ?? txn.paid_amount ?? '').toLowerCase();

            return (
                email.includes(query) || orderId.includes(query) || rrn.includes(query) || status.includes(query) || amount.includes(query)
            );
        });
    }, [transactions, debouncedSearch]);

    // Summary Statistics
    const stats = useMemo(() => {
        let totalAmount = 0;
        let successCount = 0;
        const uniqueEmails = new Set();

        transactions.forEach((txn) => {
            const amt = Number(txn.paid_amount ?? txn.amount ?? 0);
            totalAmount += isNaN(amt) ? 0 : amt;

            const st = String(txn.status || '').toUpperCase();
            if (st === 'SUCCESS') successCount++;

            if (txn.email) uniqueEmails.add(txn.email.toLowerCase());
        });

        const count = totalCount || transactions.length;

        return {
            totalAmount,
            successCount,
            uniqueAccounts: uniqueEmails.size,
            count
        };
    }, [transactions, totalCount]);

    // ─── Export to Excel ──────────────────────────────────────────────
    const handleExportExcel = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            let exportList = transactions;

            try {
                const exportPayload = {
                    from_date: fromDate,
                    to_date: toDate,
                    page: 1,
                    per_page: Math.max(totalCount || 500, 500)
                };
                const res = await historyApi(exportPayload);
                const resData = res?.data;
                if (Array.isArray(resData?.data)) exportList = resData.data;
                else if (Array.isArray(resData?.data?.data)) exportList = resData.data.data;
            } catch (err) {
                console.warn('Fallback to currently loaded transactions for export:', err);
            }

            if (!exportList || exportList.length === 0) {
                globalNotifyError('No transaction records available to export for this date range');
                return;
            }

            const rows = exportList.map((item) => ({
                'Order ID': item.order_id || '',
                'Settled At': item.settled_at ? moment(item.settled_at).format('YYYY-MM-DD HH:mm:ss') : item.created_at || '',
                'Email ID': item.email || '',
                'Amount (₹)': Number(item.amount ?? item.paid_amount ?? 0),
                'Paid Amount (₹)': Number(item.paid_amount ?? item.amount ?? 0),
                'RRN / Reference': item.rrn || '',
                Status: (item.status || 'SUCCESS').toUpperCase()
            }));

            const worksheet = XLSX.utils.json_to_sheet(rows);
            worksheet['!cols'] = [
                { wch: 32 }, // Order ID
                { wch: 22 }, // Settled At
                { wch: 32 }, // Email ID
                { wch: 16 }, // Amount
                { wch: 16 }, // Paid Amount
                { wch: 28 }, // RRN
                { wch: 14 } // Status
            ];

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Repayments');
            XLSX.writeFile(workbook, `Repayment_Transactions_${fromDate}_to_${toDate}.xlsx`);
            globalNotifySuccess('Transaction history exported to Excel successfully');
        } catch (error) {
            console.error('Failed to export transaction history:', error);
            handleCatchError(error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 8 }}>
            {/* Header Banner */}
            <TransactionHistoryBanner onBack={() => navigate('/HR/credit-limits')} />

            {/* Main Content Container */}
            <Container maxWidth="xl" sx={{ mt: -5 }}>
                {/* Stats Summary Cards */}
                <TransactionHistoryStats stats={stats} />

                {/* Table & Controls Card */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                        bgcolor: '#fff',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                    }}
                >
                    {/* Filters Toolbar */}
                    <TransactionHistoryToolbar
                        fromDate={fromDate}
                        toDate={toDate}
                        onFromDateChange={setFromDate}
                        onToDateChange={setToDate}
                        onApplyFilter={handleApplyFilter}
                        onResetFilter={handleResetFilter}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        onExport={handleExportExcel}
                        isExporting={isExporting}
                        exportDisabled={displayedTransactions.length === 0}
                        onRefresh={() => fetchTransactionHistory(page, rowsPerPage, fromDate, toDate)}
                        loading={loading}
                    />

                    {/* Table with Pagination */}
                    <TransactionHistoryTable
                        loading={loading}
                        transactions={displayedTransactions}
                        totalCount={totalCount}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={(newPage) => {
                            setPage(newPage);
                            fetchTransactionHistory(newPage, rowsPerPage, fromDate, toDate);
                        }}
                        onRowsPerPageChange={(newPerPage) => {
                            setRowsPerPage(newPerPage);
                            setPage(0);
                            fetchTransactionHistory(0, newPerPage, fromDate, toDate);
                        }}
                        fromDate={fromDate}
                        toDate={toDate}
                        onResetFilter={handleResetFilter}
                    />
                </Paper>
            </Container>
        </Box>
    );
};

export default TransactionHistory;
